import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import CalendarPicker from './CalendarPicker';

const DateFilter = ({ onDateFilterChange, isVisible, onToggle }) => {

  // Set default date range to last 7 days (new default)
  const getDefaultDateRange = () => {
    const now = new Date();
    const sevenDaysAgo = new Date(now);
    sevenDaysAgo.setDate(now.getDate() - 7);

    return {
      startDate: sevenDaysAgo.toISOString().split('T')[0],
      endDate: now.toISOString().split('T')[0]
    };
  };

  const [dateRange, setDateRange] = useState(getDefaultDateRange());
  const [isActive, setIsActive] = useState(false);
  const [showStartCalendar, setShowStartCalendar] = useState(false);
  const [showEndCalendar, setShowEndCalendar] = useState(false);
  const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0 });

  // Refs for calendar buttons
  const startButtonRef = React.useRef(null);
  const endButtonRef = React.useRef(null);

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Calculate calendar position to stay within viewport
  const calculateCalendarPosition = (buttonRef) => {
    if (!buttonRef.current) return { top: 0, left: 0 };

    const rect = buttonRef.current.getBoundingClientRect();
    const calendarHeight = 360; // Approximate calendar height
    const calendarWidth = 300; // Approximate calendar width
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const margin = 10; // Margin from viewport edges

    let top = rect.bottom + margin;
    let left = rect.left;

    // Check if calendar would go below viewport
    if (top + calendarHeight > viewportHeight) {
      // Position above the button instead
      top = rect.top - calendarHeight - margin;
    }

    // Check if calendar would go right of viewport
    if (left + calendarWidth > viewportWidth) {
      // Align to right edge
      left = viewportWidth - calendarWidth - margin;
    }

    // Ensure calendar doesn't go left of viewport
    if (left < margin) {
      left = margin;
    }

    return { top, left };
  };

  // Handle date range changes
  const handleDateChange = (field, value) => {
    const newRange = { ...dateRange, [field]: value };
    setDateRange(newRange);

    if (isActive) {
      onDateFilterChange(newRange);
    }
  };

  // Toggle filter activation
  const handleToggleActive = () => {
    const newActiveState = !isActive;
    setIsActive(newActiveState);

    if (newActiveState) {
      onDateFilterChange(dateRange);
    } else {
      onDateFilterChange(null); // Clear filter
    }
  };

  // Reset to default range
  const handleReset = () => {
    const defaultRange = getDefaultDateRange();
    setDateRange(defaultRange);
    if (isActive) {
      onDateFilterChange(defaultRange);
    }
  };

  
  // Click outside to close calendars
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is inside calendar portal
      const calendarPortal = document.querySelector('[data-calendar-portal]');
      if (calendarPortal && calendarPortal.contains(event.target)) {
        return; // Don't close if clicking inside calendar
      }

      if (showStartCalendar && startButtonRef.current && !startButtonRef.current.contains(event.target)) {
        setShowStartCalendar(false);
      }
      if (showEndCalendar && endButtonRef.current && !endButtonRef.current.contains(event.target)) {
        setShowEndCalendar(false);
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        setShowStartCalendar(false);
        setShowEndCalendar(false);
      }
    };

    if (showStartCalendar || showEndCalendar) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
  }, [showStartCalendar, showEndCalendar]);

  // Quick date range presets
  const applyPreset = (presetType) => {
    const now = new Date();
    let startDate;

    switch (presetType) {
      case '7days':
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 7);
        break;
      case '30days':
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 30);
        break;
      case 'all':
        // Set a far past date for "all" - 2 years ago
        startDate = new Date(now);
        startDate.setFullYear(now.getFullYear() - 2);
        break;
      default:
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 7);
    }

    const newRange = {
      startDate: startDate.toISOString().split('T')[0],
      endDate: now.toISOString().split('T')[0]
    };

    setDateRange(newRange);

    // Auto-activate filter when preset is selected
    if (!isActive) {
      setIsActive(true);
    }
    onDateFilterChange(newRange);
  };

  if (!isVisible) {
    // Collapsed state - button with text and active indicator
    return (
      <div
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          zIndex: 1005,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <button
          onClick={onToggle}
          style={{
            padding: '10px 16px',
            borderRadius: '24px',
            background: isActive ?
              'linear-gradient(135deg, rgba(255, 120, 60, 0.9), rgba(255, 80, 30, 0.8))' :
              'linear-gradient(135deg, rgba(60, 60, 60, 0.9), rgba(40, 40, 40, 0.8))',
            border: isActive ?
              '2px solid rgba(255, 120, 60, 0.7)' :
              '2px solid rgba(255, 120, 60, 0.4)',
            color: 'white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
            transition: 'all 0.3s ease',
            fontSize: '14px',
            fontWeight: '600',
            fontFamily: 'var(--font-display)',
            textShadow: '0 1px 3px rgba(0, 0, 0, 0.7)',
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'scale(1.05)';
            e.target.style.boxShadow = isActive ?
              '0 6px 16px rgba(255, 120, 60, 0.5)' :
              '0 6px 16px rgba(0, 0, 0, 0.5)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'scale(1)';
            e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.4)';
          }}
          title="Filtruj tematy po dacie"
        >
          📅
          Filtruj po dacie
          {isActive && (
            <span
              style={{
                background: 'rgba(255, 255, 255, 0.9)',
                color: 'rgba(255, 80, 30, 1)',
                padding: '2px 6px',
                borderRadius: '10px',
                fontSize: '11px',
                fontWeight: '700',
                textShadow: 'none',
              }}
            >
              AKTYWNY
            </span>
          )}
        </button>
      </div>
    );
  }

  const filterContent = (
    <div
      style={{
        position: 'fixed',
        bottom: '100px',
        right: '20px',
        width: '320px',
        maxWidth: '90vw',
        background: 'linear-gradient(135deg, rgba(40, 20, 10, 0.95), rgba(30, 15, 8, 0.98))',
        border: '1px solid rgba(255, 120, 60, 0.4)',
        borderRadius: '16px',
        padding: '20px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.6)',
        zIndex: 1005,
        fontFamily: 'var(--font-display)',
      }}
    >
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
      }}>
        <h3 style={{
          margin: 0,
          color: 'rgba(255, 220, 160, 0.9)',
          fontSize: '16px',
          fontWeight: '600',
          textShadow: '0 1px 3px rgba(0, 0, 0, 0.7)',
        }}>
          Filtruj tematy po dacie
        </h3>
        <button
          onClick={onToggle}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'rgba(255, 180, 100, 0.8)',
            cursor: 'pointer',
            fontSize: '20px',
            padding: '4px',
            borderRadius: '4px',
            transition: 'background-color 0.2s ease',
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255, 120, 60, 0.2)'}
          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
        >
          ✕
        </button>
      </div>

      {/* Active toggle and Clear button */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: '16px',
        gap: '12px',
      }}>
        <button
          onClick={handleToggleActive}
          style={{
            flex: 1,
            padding: '10px 16px',
            background: isActive ?
              'linear-gradient(135deg, rgba(255, 120, 60, 0.8), rgba(255, 80, 30, 0.7))' :
              'linear-gradient(135deg, rgba(60, 60, 60, 0.8), rgba(40, 40, 40, 0.7))',
            border: '1px solid rgba(255, 120, 60, 0.4)',
            borderRadius: '8px',
            color: 'white',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            transition: 'all 0.2s ease',
            textShadow: '0 1px 3px rgba(0, 0, 0, 0.7)',
          }}
        >
          {isActive ? 'Aktywny ✅' : 'Nieaktywny'}
        </button>
        <button
          onClick={handleReset}
          style={{
            padding: '10px',
            background: 'linear-gradient(135deg, rgba(40, 40, 40, 0.8), rgba(30, 30, 30, 0.7))',
            border: '1px solid rgba(255, 120, 60, 0.3)',
            borderRadius: '8px',
            color: 'rgba(255, 180, 100, 0.9)',
            cursor: 'pointer',
            fontSize: '12px',
            transition: 'all 0.2s ease',
          }}
          title="Resetuj do ostatnich 7 dni"
        >
          🔄
        </button>
        <button
          onClick={() => {
            setIsActive(false);
            onDateFilterChange(null);
          }}
          style={{
            padding: '10px',
            background: 'linear-gradient(135deg, rgba(180, 40, 40, 0.8), rgba(150, 20, 20, 0.7))',
            border: '1px solid rgba(255, 120, 60, 0.3)',
            borderRadius: '8px',
            color: 'rgba(255, 180, 100, 0.9)',
            cursor: 'pointer',
            fontSize: '12px',
            transition: 'all 0.2s ease',
          }}
          title="Wyczyść filtr"
        >
          ❌
        </button>
      </div>

      {/* Custom calendar inputs */}
      <div style={{
        marginBottom: '16px',
      }}>
        <div style={{ marginBottom: '12px' }}>
          <button
            ref={startButtonRef}
            type="button"
            onClick={() => {
              const position = calculateCalendarPosition(startButtonRef);
              setCalendarPosition(position);
              setShowStartCalendar(!showStartCalendar);
              setShowEndCalendar(false);
            }}
            style={{
              width: '100%',
              padding: '10px 12px',
              background: 'rgba(20, 10, 5, 0.8)',
              border: '1px solid rgba(255, 120, 60, 0.3)',
              borderRadius: '6px',
              color: 'rgba(255, 220, 160, 0.9)',
              fontSize: '14px',
              fontFamily: 'var(--font-display)',
              cursor: 'pointer',
              textAlign: 'left',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span>Data początkowa: {formatDate(dateRange.startDate)}</span>
            <span style={{ fontSize: '18px' }}>📅</span>
          </button>
        </div>

        <div>
          <button
            ref={endButtonRef}
            type="button"
            onClick={() => {
              const position = calculateCalendarPosition(endButtonRef);
              setCalendarPosition(position);
              setShowEndCalendar(!showEndCalendar);
              setShowStartCalendar(false);
            }}
            style={{
              width: '100%',
              padding: '10px 12px',
              background: 'rgba(20, 10, 5, 0.8)',
              border: '1px solid rgba(255, 120, 60, 0.3)',
              borderRadius: '6px',
              color: 'rgba(255, 220, 160, 0.9)',
              fontSize: '14px',
              fontFamily: 'var(--font-display)',
              cursor: 'pointer',
              textAlign: 'left',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span>Data końcowa: {formatDate(dateRange.endDate)}</span>
            <span style={{ fontSize: '18px' }}>📅</span>
          </button>
        </div>
      </div>

      {/* Preset buttons */}
      <div style={{
        marginBottom: '16px',
      }}>
        <div style={{
          color: 'rgba(255, 200, 140, 0.8)',
          fontSize: '12px',
          marginBottom: '8px',
          fontWeight: '500',
        }}>
          Presety:
        </div>
        <div style={{
          display: 'flex',
          gap: '8px',
          flexWrap: 'wrap',
        }}>
          <button
            onClick={() => applyPreset('7days')}
            style={{
              padding: '8px 12px',
              background: 'linear-gradient(135deg, rgba(255, 120, 60, 0.7), rgba(255, 80, 30, 0.6))',
              border: '1px solid rgba(255, 120, 60, 0.4)',
              borderRadius: '8px',
              color: 'white',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '600',
              transition: 'all 0.2s ease',
              fontFamily: 'var(--font-display)',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)',
            }}
          >
            Ostatnie 7 dni
          </button>
          <button
            onClick={() => applyPreset('30days')}
            style={{
              padding: '8px 12px',
              background: 'linear-gradient(135deg, rgba(40, 40, 40, 0.8), rgba(30, 30, 30, 0.7))',
              border: '1px solid rgba(255, 120, 60, 0.3)',
              borderRadius: '8px',
              color: 'rgba(255, 180, 100, 0.9)',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '600',
              transition: 'all 0.2s ease',
              fontFamily: 'var(--font-display)',
            }}
          >
            Ostatnie 30 dni
          </button>
          <button
            onClick={() => applyPreset('all')}
            style={{
              padding: '8px 12px',
              background: 'linear-gradient(135deg, rgba(40, 40, 40, 0.8), rgba(30, 30, 30, 0.7))',
              border: '1px solid rgba(255, 120, 60, 0.3)',
              borderRadius: '8px',
              color: 'rgba(255, 180, 100, 0.9)',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '600',
              transition: 'all 0.2s ease',
              fontFamily: 'var(--font-display)',
            }}
          >
            Wszystko
          </button>
        </div>
      </div>

      {/* Current selection display */}
      {isActive && (
        <div style={{
          padding: '8px 12px',
          background: 'rgba(255, 120, 60, 0.1)',
          border: '1px solid rgba(255, 120, 60, 0.2)',
          borderRadius: '6px',
          fontSize: '11px',
          color: 'rgba(255, 200, 140, 0.8)',
          textAlign: 'center',
        }}>
          {formatDate(dateRange.startDate)} - {formatDate(dateRange.endDate)}
        </div>
      )}
    </div>
  );

  // Render calendars using portal to avoid z-index issues
  const renderCalendarPortal = () => {
    if (!showStartCalendar && !showEndCalendar) return null;

    return createPortal(
      <div
        data-calendar-portal="true"
        style={{
          position: 'fixed',
          top: calendarPosition.top,
          left: calendarPosition.left,
          zIndex: 10000,
        }}
      >
        <div
          style={{
            background: 'linear-gradient(135deg, rgba(40, 20, 10, 0.98), rgba(30, 15, 8, 0.99))',
            border: '1px solid rgba(255, 120, 60, 0.4)',
            borderRadius: '12px',
            padding: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.7)',
            minWidth: '280px',
            maxWidth: '90vw',
          }}
        >
          <CalendarPicker
            selectedDate={showStartCalendar ? dateRange.startDate : dateRange.endDate}
            onDateSelect={(date) => {
              if (showStartCalendar) {
                handleDateChange('startDate', date);
                setShowStartCalendar(false);
              } else {
                handleDateChange('endDate', date);
                setShowEndCalendar(false);
              }
            }}
            label=""
          />
        </div>
      </div>,
      document.body
    );
  };

  return (
    <>
      {filterContent}
      {renderCalendarPortal()}
    </>
  );
};

export default DateFilter;